import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Send, Paperclip, Search, ArrowRightLeft, CheckCircle2, UserPlus, MessageSquarePlus,
  Moon, Bot, X, Zap, FileText, Download, Phone, Users,
} from 'lucide-react';
import { jarvis, jarvisAoFechar, ESTADOS, formatarTelefone, hora, tempoDesde } from './api';

const LIMITE_ARQUIVO = 3.5 * 1024 * 1024; // o servidor aceita 5 MB por pedido (base64 cresce ~33%)

function bip() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination); o.frequency.value = 880; g.gain.value = 0.08;
    o.start(); o.stop(ctx.currentTime + 0.18);
  } catch { /* navegador sem áudio */ }
}

function Midia({ msg }) {
  const [url, setUrl] = useState(null);
  const [erro, setErro] = useState('');
  const carregar = useCallback(() => {
    jarvis('arquivo', { msg: msg.id }).then(r => setUrl(r.url)).catch(e => setErro(e.message));
  }, [msg.id]);
  useEffect(() => { if (msg.tipo === 'imagem' || msg.tipo === 'figurinha' || msg.tipo === 'audio') carregar(); }, [msg.tipo, carregar]);

  if (!msg.midia_path) return <em className="jv-midia-falta">[{msg.tipo} — arquivo não disponível]</em>;
  if (erro) return <em className="jv-midia-falta">{erro}</em>;
  if (msg.tipo === 'imagem' || msg.tipo === 'figurinha') {
    return url ? <a href={url} target="_blank" rel="noreferrer"><img className="jv-img" src={url} alt="foto" /></a> : <div className="jv-img jv-img-vazia">carregando foto…</div>;
  }
  if (msg.tipo === 'audio') return url ? <audio controls src={url} className="jv-audio" /> : <span>carregando áudio…</span>;
  if (url) {
    if (msg.tipo === 'video') return <video controls src={url} className="jv-img" />;
    return <a className="jv-doc" href={url} target="_blank" rel="noreferrer"><FileText size={18} /> {msg.midia_nome || 'documento'} <Download size={14} /></a>;
  }
  return <button className="jv-doc" onClick={carregar}><FileText size={18} /> {msg.midia_nome || (msg.tipo === 'video' ? 'vídeo' : 'documento')} — abrir</button>;
}

function Modal({ titulo, aoFechar, children }) {
  return (
    <div className="jv-modal-fundo" onMouseDown={aoFechar}>
      <div className="jv-modal" onMouseDown={e => e.stopPropagation()}>
        <div className="jv-modal-cab"><strong>{titulo}</strong><button onClick={aoFechar}><X size={18} /></button></div>
        {children}
      </div>
    </div>
  );
}

export default function Operador({ eu, voltar }) {
  const [painel, setPainel] = useState({ conversas: [], encerradas: [], filas: [] });
  const [aba, setAba] = useState('atendimentos');
  const [busca, setBusca] = useState('');
  const [selId, setSelId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [conv, setConv] = useState(null);
  const [anteriores, setAnteriores] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [textos, setTextos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [verTextos, setVerTextos] = useState(false);
  const [modal, setModal] = useState(null); // 'transferir' | 'encerrar' | 'nova'
  const versao = useRef('');
  const ultimoId = useRef(0);
  const esperaAntes = useRef(null);
  const fimRef = useRef(null);
  const arquivoRef = useRef(null);

  // Lista de conversas: a cada 3 s com a aba visível; o servidor só manda tudo se algo mudou
  const atualizarPainel = useCallback(async () => {
    if (document.visibilityState === 'hidden') return;
    try {
      const r = await jarvis('painel', { v: versao.current });
      if (r.igual) return;
      versao.current = r.versao;
      setPainel(r);
      const espera = r.conversas.filter(c => c.estado === 'espera').length;
      if (esperaAntes.current !== null && espera > esperaAntes.current) bip();
      esperaAntes.current = espera;
      setErro('');
    } catch (e) { setErro(e.message); }
  }, []);

  useEffect(() => {
    atualizarPainel();
    jarvis('textos').then(r => setTextos(r.textos)).catch(() => {});
    jarvis('categorias').then(r => setCategorias(r.categorias)).catch(() => {});
    const t = setInterval(atualizarPainel, 3000);
    document.addEventListener('visibilitychange', atualizarPainel);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', atualizarPainel); };
  }, [atualizarPainel]);

  // Sessão no módulo (relatório de login/logout): entra ao abrir, sinal a cada minuto
  // (mesmo com a aba em segundo plano) e sai ao voltar/fechar a página.
  useEffect(() => {
    jarvis('sinal').catch(() => {});
    const t = setInterval(() => jarvis('sinal').catch(() => {}), 60000);
    const aoFechar = () => jarvisAoFechar('sairOperador');
    window.addEventListener('pagehide', aoFechar);
    return () => { clearInterval(t); window.removeEventListener('pagehide', aoFechar); jarvisAoFechar('sairOperador'); };
  }, []);

  const selRef = useRef(null);
  const carregadoRef = useRef(null); // conversa cuja carga inicial já terminou
  const carregarMensagens = useCallback(async (id, doZero) => {
    if (!id || (!doZero && carregadoRef.current !== id)) return;
    try {
      const r = await jarvis('mensagens', { id, depois: doZero ? 0 : ultimoId.current });
      if (selRef.current !== id) return; // trocou de conversa enquanto buscava
      setConv(r.conversa);
      if (r.mensagens.length || doZero) {
        setMsgs(prev => {
          if (doZero) return r.mensagens;
          const ids = new Set(prev.map(m => m.id));
          return [...prev, ...r.mensagens.filter(m => !ids.has(m.id))];
        });
        if (r.mensagens.length) ultimoId.current = Math.max(ultimoId.current, r.mensagens[r.mensagens.length - 1].id);
      }
      if (doZero) carregadoRef.current = id;
      if (r.conversa.nao_lidas && r.conversa.atendente_email === eu.email) jarvis('lido', { id }).catch(() => {});
    } catch (e) { setErro(e.message); }
  }, [eu.email]);

  // Abriu outra conversa: carrega do zero + histórico do contato
  useEffect(() => {
    ultimoId.current = 0; carregadoRef.current = null; selRef.current = selId;
    setMsgs([]); setConv(null); setAnteriores([]); setVerTextos(false);
    if (!selId) return;
    carregarMensagens(selId, true);
    jarvis('historico', { id: selId }).then(r => setAnteriores(r.anteriores)).catch(() => {});
  }, [selId, carregarMensagens]);

  // Painel mudou: busca só as mensagens novas da conversa aberta
  useEffect(() => { if (selId) carregarMensagens(selId, false); }, [painel, selId, carregarMensagens]);
  useEffect(() => { fimRef.current?.scrollIntoView({ block: 'end' }); }, [msgs.length]);

  const nomeFila = id => painel.filas.find(f => f.id === id)?.nome || 'Sem fila';
  // Abas (19/10, Alexandre): "Atendimentos" = os que estão comigo + os que já escolheram a fila;
  // o robô e os grupos ficam em abas separadas; sem "Todas".
  const listas = useMemo(() => {
    const todas = painel.conversas || [];
    const meus = todas.filter(c => c.estado === 'atendimento' && c.atendente_email === eu.email);
    const fila = todas.filter(c => c.estado === 'espera').sort((a, b) => String(a.entrou_fila_em || a.criado_em).localeCompare(String(b.entrou_fila_em || b.criado_em)));
    return {
      meus, fila, atendimentos: [...meus, ...fila],
      robo: todas.filter(c => c.estado === 'bot'),
      grupos: todas.filter(c => c.estado === 'grupo'),
      encerradas: painel.encerradas || [],
    };
  }, [painel, eu.email]);
  const filtrar = arr => (arr || []).filter(c => {
    if (!busca) return true;
    const b = busca.toLowerCase();
    return [c.nome, c.telefone, c.cooperado?.nome, String(c.protocolo)].some(v => String(v || '').toLowerCase().includes(b));
  });
  const lista = filtrar(listas[aba]);
  const grupoComNovas = listas.grupos.filter(c => c.nao_lidas > 0).length;

  const acao = async (nome, dados, depois) => {
    try {
      setErro('');
      const r = await jarvis(nome, dados);
      versao.current = '';
      await atualizarPainel();
      if (selId) await carregarMensagens(selId, false);
      depois?.(r);
    } catch (e) { setErro(e.message); }
  };

  const enviar = async () => {
    const t = texto.trim();
    if (!t || !conv || enviando) return;
    setEnviando(true);
    await acao('enviar', { id: conv.id, texto: t }, () => setTexto(''));
    setEnviando(false);
  };

  const escolherArquivo = async e => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || !conv) return;
    if (f.size > LIMITE_ARQUIVO) { setErro('Arquivo grande demais (máximo 3,5 MB).'); return; }
    const base64 = await new Promise((ok, falha) => {
      const fr = new FileReader();
      fr.onload = () => ok(String(fr.result).split(',')[1]);
      fr.onerror = falha;
      fr.readAsDataURL(f);
    });
    setEnviando(true);
    await acao('enviarArquivo', { id: conv.id, base64, mime: f.type || 'application/octet-stream', nome: f.name, legenda: texto.trim() || undefined }, () => setTexto(''));
    setEnviando(false);
  };

  const itemConversa = c => (
    <button key={c.id} className={`jv-item ${selId === c.id ? 'sel' : ''}`} onClick={() => setSelId(c.id)}>
      <div className="jv-avatar" style={c.estado === 'grupo' ? { background: '#dbeafe', color: '#1d4ed8' } : undefined}>{c.estado === 'grupo' ? <Users size={16} /> : (c.cooperado?.nome || c.nome || '?').slice(0, 1).toUpperCase()}</div>
      <div className="jv-item-meio">
        <div className="jv-item-linha">
          <strong>{c.cooperado?.nome || c.nome || formatarTelefone(c.telefone)}</strong>
          <span className="jv-item-hora">{hora(c.ultima_msg_em || c.criado_em)}</span>
        </div>
        <div className="jv-item-linha">
          <span className="jv-item-msg">{c.ultima_msg || '—'}</span>
          {c.nao_lidas > 0 && <span className="jv-nao-lidas">{c.nao_lidas}</span>}
        </div>
        <div className="jv-item-tags">
          {c.fora_horario && <span className="jv-tag jv-tag-noite"><Moon size={11} /> sobreaviso</span>}
          {c.estado === 'grupo' ? <span className="jv-tag">grupo</span> : <span className="jv-tag">{nomeFila(c.fila_id)}</span>}
          {c.estado === 'espera' && <span className="jv-tag jv-tag-espera">esperando {tempoDesde(c.entrou_fila_em || c.criado_em)}</span>}
          {c.estado === 'atendimento' && c.atendente_email !== eu.email && <span className="jv-tag">{c.atendente_nome}</span>}
          {c.estado === 'encerrada' && c.categoria && <span className="jv-tag">{c.categoria}</span>}
        </div>
      </div>
    </button>
  );

  const textosDaConversa = textos.filter(t => !t.fila_id || t.fila_id === conv?.fila_id);
  const minha = conv && conv.estado === 'atendimento' && conv.atendente_email === eu.email;
  const comOutro = conv && conv.estado === 'atendimento' && conv.atendente_email !== eu.email;

  return (
    <div className="jv-operador">
      <div className="jv-barra">
        {voltar}
        <strong>Operador</strong>
        <span className="jv-online">● online como {eu.nome}</span>
        <div className="jv-barra-dir">
          <button className="jv-btn" onClick={() => setModal('nova')}><MessageSquarePlus size={16} /> Nova conversa</button>
        </div>
      </div>
      {erro && <div className="jv-aviso jv-aviso-erro jv-aviso-fixo">{erro} <button onClick={() => setErro('')}><X size={14} /></button></div>}

      <div className="jv-colunas">
        {/* ── Lista ─────────────────────────────── */}
        <aside className="jv-lista">
          <div className="jv-abas">
            {[['atendimentos', 'Atendimentos'], ['robo', 'No robô'], ['grupos', 'Grupos'], ['encerradas', 'Encerradas']].map(([k, r]) => {
              const n = k === 'grupos' ? grupoComNovas : k !== 'encerradas' ? listas[k]?.length : 0;
              return (
                <button key={k} className={aba === k ? 'ativa' : ''} onClick={() => setAba(k)}>
                  {r}{n ? <span className="jv-cont">{n}</span> : null}
                </button>
              );
            })}
          </div>
          <div className="jv-busca"><Search size={15} /><input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar nome, telefone, protocolo…" /></div>
          <div className="jv-itens">
            {aba === 'atendimentos' ? (
              <>
                <div className="jv-secao">Comigo <span>{filtrar(listas.meus).length}</span></div>
                {filtrar(listas.meus).length === 0 && <div className="jv-vazio">Nenhum atendimento com você.</div>}
                {filtrar(listas.meus).map(c => itemConversa(c))}
                <div className="jv-secao">Na fila — já escolheram <span>{filtrar(listas.fila).length}</span></div>
                {filtrar(listas.fila).length === 0 && <div className="jv-vazio">Ninguém esperando.</div>}
                {filtrar(listas.fila).map(c => itemConversa(c))}
              </>
            ) : (
              <>
                {lista.length === 0 && <div className="jv-vazio">{aba === 'grupos' ? 'Nenhum grupo com mensagem ainda (aparecem quando alguém escreve no grupo ou você manda por "Nova conversa").' : 'Nenhuma conversa aqui.'}</div>}
                {lista.map(c => itemConversa(c))}
              </>
            )}
          </div>
        </aside>

        {/* ── Chat ──────────────────────────────── */}
        <section className="jv-chat">
          {!conv ? (
            <div className="jv-chat-vazio"><Bot size={56} /><p>Escolha uma conversa à esquerda.</p></div>
          ) : (
            <>
              <div className="jv-chat-cab">
                <div>
                  <strong>{conv.cooperado?.nome || conv.nome || formatarTelefone(conv.telefone)}</strong>
                  <span className="jv-sub">
                    {conv.estado === 'grupo' ? 'Grupo do WhatsApp · todos os operadores veem e respondem · ' : <>{formatarTelefone(conv.telefone)} · protocolo {conv.protocolo} · {nomeFila(conv.fila_id)} ·{' '}</>}
                    <b style={{ color: ESTADOS[conv.estado]?.cor }}>{ESTADOS[conv.estado]?.rotulo}</b>
                    {comOutro && ` com ${conv.atendente_nome}`}
                  </span>
                </div>
                <div className="jv-chat-acoes">
                  {conv.estado !== 'encerrada' && conv.estado !== 'grupo' && !minha && (
                    <button className="jv-btn jv-btn-primario" onClick={() => acao('assumir', { id: conv.id, forcar: comOutro })}>
                      <UserPlus size={16} /> {comOutro ? 'Puxar para mim' : 'Assumir'}
                    </button>
                  )}
                  {conv.estado !== 'encerrada' && conv.estado !== 'grupo' && <button className="jv-btn" onClick={() => setModal('transferir')}><ArrowRightLeft size={16} /> Transferir</button>}
                  {conv.estado !== 'encerrada' && conv.estado !== 'grupo' && <button className="jv-btn jv-btn-perigo" onClick={() => setModal('encerrar')}><CheckCircle2 size={16} /> Encerrar</button>}
                </div>
              </div>

              <div className="jv-mensagens">
                {msgs.map(m => (
                  m.direcao === 'sistema'
                    ? <div key={m.id} className="jv-msg-sistema">{m.texto} · {hora(m.criado_em)}</div>
                    : (
                      <div key={m.id} className={`jv-msg ${m.direcao === 'saida' ? 'saida' : 'entrada'}`}>
                        {m.direcao === 'saida' && <div className="jv-msg-autor">{m.autor === 'Robô' ? '🤖 Robô' : m.autor}</div>}
                        {m.direcao === 'entrada' && conv.estado === 'grupo' && m.autor && <div className="jv-msg-autor" style={{ color: '#1d4ed8' }}>{m.autor}</div>}
                        {m.midia_path || ['imagem', 'audio', 'video', 'documento', 'figurinha'].includes(m.tipo) ? <Midia msg={m} /> : null}
                        {m.texto && <div className="jv-msg-texto">{m.texto}</div>}
                        <div className="jv-msg-hora">{hora(m.criado_em)}{m.status === 'erro' && <span className="jv-msg-erro"> · não enviada</span>}</div>
                      </div>
                    )
                ))}
                <div ref={fimRef} />
              </div>

              {conv.estado === 'encerrada' ? (
                <div className="jv-encerrada">Conversa encerrada{conv.categoria ? ` — ${conv.categoria}` : ''}. Se o contato escrever de novo, abre um novo protocolo.</div>
              ) : (
                <div className="jv-entrada">
                  {verTextos && (
                    <div className="jv-textos-prontos">
                      {textosDaConversa.length === 0 && <div className="jv-vazio">Nenhum texto pronto (o supervisor cadastra).</div>}
                      {textosDaConversa.map(t => (
                        <button key={t.id} onClick={() => { setTexto(t.texto); setVerTextos(false); }}><strong>{t.titulo}</strong><span>{t.texto}</span></button>
                      ))}
                    </div>
                  )}
                  <button className="jv-icone" title="Textos prontos" onClick={() => setVerTextos(v => !v)}><Zap size={20} /></button>
                  <button className="jv-icone" title="Enviar arquivo (até 3,5 MB)" onClick={() => arquivoRef.current?.click()}><Paperclip size={20} /></button>
                  <input ref={arquivoRef} type="file" hidden onChange={escolherArquivo} />
                  <textarea
                    value={texto} rows={1} placeholder={conv.estado === 'grupo' ? 'Mensagem para o grupo… (Enter envia)' : minha ? 'Digite a mensagem… (Enter envia, Shift+Enter quebra linha)' : 'Ao enviar, você assume o atendimento'}
                    onChange={e => setTexto(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
                  />
                  <button className="jv-enviar" disabled={!texto.trim() || enviando} onClick={enviar}><Send size={18} /></button>
                </div>
              )}
            </>
          )}
        </section>

        {/* ── Contato ───────────────────────────── */}
        <aside className="jv-contato">
          {conv ? (
            <>
              <div className="jv-contato-cab">
                <div className="jv-avatar jv-avatar-g">{(conv.cooperado?.nome || conv.nome || '?').slice(0, 1).toUpperCase()}</div>
                <strong>{conv.cooperado?.nome || conv.nome || 'Sem nome'}</strong>
                <span><Phone size={13} /> {formatarTelefone(conv.telefone)}</span>
                {conv.nome && conv.cooperado?.nome && <span className="jv-sub">no WhatsApp: {conv.nome}</span>}
              </div>
              {conv.estado === 'grupo' ? (
                <div className="jv-bloco"><h4>Grupo do WhatsApp</h4><p className="jv-sub">Sem robô e sem fila: qualquer operador responde. Não entra nos relatórios de atendimento.</p></div>
              ) : conv.cooperado ? (
                <div className="jv-bloco">
                  <h4>Cooperado reconhecido</h4>
                  <p><b>{conv.cooperado.tipo || '—'}</b></p>
                  <p>{conv.cooperado.cooperativa || '—'}</p>
                  <p>RH: {conv.cooperado.statusRH || '—'}</p>
                  {conv.cooperado.outros > 0 && <p className="jv-sub">+{conv.cooperado.outros} cadastro(s) com o mesmo telefone</p>}
                </div>
              ) : (
                <div className="jv-bloco"><h4>Cooperado</h4><p className="jv-sub">Telefone não encontrado no Portal Cooperativa.</p></div>
              )}
              <div className="jv-bloco">
                <h4>Este atendimento</h4>
                <p>Protocolo <b>{conv.protocolo}</b></p>
                <p>Aberto {hora(conv.criado_em)}{conv.fora_horario ? ' 🌙 fora do horário' : ''}</p>
                {conv.assumido_em && <p>Assumido {hora(conv.assumido_em)}</p>}
              </div>
              <div className="jv-bloco">
                <h4>Atendimentos anteriores</h4>
                {anteriores.length === 0 && <p className="jv-sub">Nenhum.</p>}
                {anteriores.map(a => (
                  <button key={a.id} className="jv-anterior" onClick={() => setSelId(a.id)}>
                    #{a.protocolo} · {hora(a.criado_em)} · {a.categoria || ESTADOS[a.estado]?.rotulo}
                  </button>
                ))}
              </div>
            </>
          ) : <div className="jv-vazio">Dados do contato aparecem aqui.</div>}
        </aside>
      </div>

      {modal === 'transferir' && conv && (
        <ModalTransferir conv={conv} filas={painel.filas} aoFechar={() => setModal(null)}
          aoTransferir={d => acao('transferir', { id: conv.id, ...d }, () => { setModal(null); setSelId(null); })} />
      )}
      {modal === 'encerrar' && conv && (
        <ModalEncerrar categorias={categorias} aoFechar={() => setModal(null)}
          aoEncerrar={d => acao('encerrar', { id: conv.id, ...d }, () => setModal(null))} />
      )}
      {modal === 'nova' && (
        <ModalNova filas={painel.filas} aoFechar={() => setModal(null)}
          aoCriar={d => acao('iniciar', d, r => { setModal(null); setAba(r.grupo ? 'grupos' : 'atendimentos'); setSelId(r.id); })} />
      )}
    </div>
  );
}

function ModalTransferir({ conv, filas, aoFechar, aoTransferir }) {
  const [online, setOnline] = useState([]);
  useEffect(() => { jarvis('atendentesOnline').then(r => setOnline(r.atendentes)).catch(() => {}); }, []);
  return (
    <Modal titulo="Transferir conversa" aoFechar={aoFechar}>
      <h4 className="jv-modal-sub">Para uma fila (vai para a espera)</h4>
      <div className="jv-opcoes">
        {filas.filter(f => f.id !== conv.fila_id).map(f => <button key={f.id} onClick={() => aoTransferir({ fila: f.id })}>{f.nome}</button>)}
        {filas.length === 0 && <span className="jv-sub">Nenhuma fila cadastrada.</span>}
      </div>
      <h4 className="jv-modal-sub">Para um atendente online</h4>
      <div className="jv-opcoes">
        {online.filter(a => a.email !== conv.atendente_email).map(a => <button key={a.email} onClick={() => aoTransferir({ atendente: a.email })}>{a.nome}</button>)}
        {online.length === 0 && <span className="jv-sub">Ninguém online agora.</span>}
      </div>
    </Modal>
  );
}

function ModalEncerrar({ categorias, aoFechar, aoEncerrar }) {
  const [categoria, setCategoria] = useState('');
  const [avisar, setAvisar] = useState(true);
  return (
    <Modal titulo="Encerrar atendimento" aoFechar={aoFechar}>
      <label className="jv-campo">Categoria
        <select value={categoria} onChange={e => setCategoria(e.target.value)}>
          <option value="">{categorias.length ? 'Escolha…' : 'Sem categorias (o supervisor cadastra)'}</option>
          {categorias.map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}
        </select>
      </label>
      <label className="jv-check"><input type="checkbox" checked={avisar} onChange={e => setAvisar(e.target.checked)} /> Enviar a mensagem de encerramento com o protocolo</label>
      <div className="jv-modal-rodape">
        <button className="jv-btn" onClick={aoFechar}>Cancelar</button>
        <button className="jv-btn jv-btn-perigo" disabled={categorias.length > 0 && !categoria} onClick={() => aoEncerrar({ categoria, avisar })}>Encerrar</button>
      </div>
    </Modal>
  );
}

// Nova conversa: busca de contato (grupos do Jarvis, quem já falou com o Jarvis e cooperados do
// Portal Cooperativa) ou o número digitado.
function ModalNova({ filas, aoFechar, aoCriar }) {
  const [busca, setBusca] = useState('');
  const [achados, setAchados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [escolhido, setEscolhido] = useState(null); // { tipo, nome, telefone?, jid? }
  const [telefone, setTelefone] = useState('');
  const [fila, setFila] = useState('');
  const [texto, setTexto] = useState('');

  useEffect(() => {
    const termo = busca.trim();
    if (escolhido || (termo.length < 2 && termo.replace(/\D/g, '').length < 3)) { setAchados([]); return undefined; }
    setBuscando(true);
    const t = setTimeout(() => {
      jarvis('contatos', { busca: termo }).then(r => setAchados(r.contatos || [])).catch(() => setAchados([])).finally(() => setBuscando(false));
    }, 300);
    return () => clearTimeout(t);
  }, [busca, escolhido]);

  const ehGrupo = escolhido?.tipo === 'grupo';
  const numero = escolhido ? escolhido.telefone : telefone;
  const pode = texto.trim() && (ehGrupo || String(numero || '').replace(/\D/g, '').length >= 10);
  const ICONE = { grupo: '👥', contato: '💬', cooperado: '🩺' };

  return (
    <Modal titulo="Nova conversa" aoFechar={aoFechar}>
      {escolhido ? (
        <div className="jv-campo">Para
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 10px', background: '#f8fafc' }}>
            <span>{ICONE[escolhido.tipo]}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong>{escolhido.nome}</strong>
              <div className="jv-sub">{ehGrupo ? escolhido.detalhe : formatarTelefone(escolhido.telefone)}</div>
            </div>
            <button className="jv-btn" onClick={() => { setEscolhido(null); setBusca(''); }}>trocar</button>
          </div>
        </div>
      ) : (
        <>
          <label className="jv-campo">Buscar contato ou grupo
            <input autoFocus value={busca} onChange={e => setBusca(e.target.value)} placeholder="Nome, telefone ou nome do grupo…" />
          </label>
          {(achados.length > 0 || buscando) && (
            <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 10 }}>
              {buscando && !achados.length && <div className="jv-vazio">Buscando…</div>}
              {achados.map((c, i) => (
                <button key={i} onClick={() => setEscolhido(c)}
                  style={{ display: 'flex', width: '100%', gap: 8, alignItems: 'center', padding: '7px 10px', border: 0, borderBottom: '1px solid #f1f5f9', background: '#fff', cursor: 'pointer', textAlign: 'left' }}>
                  <span>{ICONE[c.tipo]}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nome}</strong>
                    <span className="jv-sub">{c.tipo === 'grupo' ? c.detalhe : `${formatarTelefone(c.telefone)} · ${c.detalhe}`}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {busca.trim().length >= 2 && !buscando && !achados.length && <div className="jv-sub" style={{ marginBottom: 8 }}>Nada encontrado — digite o número abaixo.</div>}
          <label className="jv-campo">…ou o telefone (DDD + número)<input value={telefone} onChange={e => setTelefone(e.target.value)} placeholder="21 99999-9999" /></label>
        </>
      )}
      {!ehGrupo && (
        <label className="jv-campo">Fila
          <select value={fila} onChange={e => setFila(e.target.value)}>
            <option value="">Sem fila</option>
            {filas.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
        </label>
      )}
      <label className="jv-campo">{ehGrupo ? 'Mensagem para o grupo' : 'Primeira mensagem'}<textarea rows={3} value={texto} onChange={e => setTexto(e.target.value)} /></label>
      <div className="jv-modal-rodape">
        <button className="jv-btn" onClick={aoFechar}>Cancelar</button>
        <button className="jv-btn jv-btn-primario" disabled={!pode} onClick={() => aoCriar(ehGrupo ? { grupo: escolhido.jid, texto } : { telefone: numero, fila, texto })}>Enviar</button>
      </div>
    </Modal>
  );
}
